<template>
	<component
		:is="element"
		ref="flow-text"
		class="flow-text"
		:class="classes"
		:data-family="family"
		:data-weight="weight"
		:data-color="color"
		:data-transform="transform"
		:data-overflow="overflow"
		:data-link="link"
		:data-inline="inline"
		:style="styleObj"
	>
		<slot />
	</component>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import { CSSProperties } from "vue/types/jsx";
import colorNames from "../utils/ColorNames";
import { highlight, unhighlight } from "./TextHighlight";

const PARAS = ["p1", "p2", "p3", "p4", "p1-para", "p2-para", "p3-para", "p4-para"] as const;

const TYPE = [...PARAS, "h1", "h2", "h3", "h4", "h5", "h6"] as const;

const WEIGHT = ["light", "thin", "regular", "book", "medium", "bold", "black"] as const;

const FAMILY = ["default", "number", "logs"] as const;

const COLOR = [
	"light",
	"default",
	"dark",
	"primary",
	"secondary",
	"success",
	"warning",
	"error",
	"black",
	"white",
	"inherit",
	...colorNames
] as const;

const TRANSFORM = ["uppercase", "none", "lowercase", "capitalize"] as const;

const OVERFLOW = ["nowrap", "wrap", "breakall", "ellipsis"] as const;

export type TypographyTypes = typeof TYPE[number];
export type TypographyColor = typeof COLOR[number];
export type TypographyWeight = typeof WEIGHT[number];
export type TypographyTransform = typeof TRANSFORM[number];
export type TypographyOverflow = typeof OVERFLOW[number];

export default defineComponent({
	name: "Typography",
	props: {
		/**
		 * Type of text <br/>
		 * 'p1' | 'p2' | 'p3' | 'p4' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | "p1-para" | "p2-para" | "p3-para" | "p4-para"
		 */
		type: {
			type: String as PropType<TypographyTypes>,
			default: "p1",
			validator(value: TypographyTypes) {
				return TYPE.includes(value);
			}
		},

		/**
		 * Font family to apply <br/>
		 * 'default' | 'number' | 'logs'
		 */
		family: {
			type: String as PropType<typeof FAMILY[number]>,
			default: "default",
			validator(value: typeof FAMILY[number]) {
				return FAMILY.includes(value);
			}
		},

		/**
		 * 'light' | 'thin' | 'regular' | 'book' | 'medium' | 'bold' | 'black'
		 */
		weight: {
			type: String as PropType<TypographyWeight>,

			default() {
				//@ts-expect-error
				if (PARAS.includes(this.type)) {
					return "regular";
				}
				return "bold";
			},

			validator(value: TypographyWeight) {
				return WEIGHT.includes(value);
			}
		},

		/**
		 * 'gray-100' | 'gray-200' | 'gray-300' | 'gray-400' | 'gray-500' | 'gray-600' | 'gray-700' | 'gray-800' | 'gray-900' | 'primary-100' | 'primary-200' | 'primary-300' | 'primary-400' | 'primary-500' | 'secondary-100' | 'secondary-200' | 'secondary-300' | 'secondary-400' | 'secondary-500' | 'warning-100' | 'warning-200' | 'warning-300' | 'warning-400' | 'warning-500' | 'success-100' | 'success-200' | 'success-300' | 'success-400' | 'success-500' | 'danger-100' | 'danger-200' | 'danger-300' | 'danger-400' | 'danger-500' | 'color1-100' | 'color1-200' | 'color1-300' | 'color2-100' | 'color2-200' | 'color2-300' | 'color3-100' | 'color3-200' | 'color3-300' | 'color4-100' | 'color4-200' | 'color4-300' | 'color5-100' | 'color5-200' | 'color5-300' | 'color6-100' | 'color6-200' | 'color6-300' | 'light' | 'default' | 'dark' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'black' | 'white' | 'inherit'
		 */
		color: {
			type: String as PropType<TypographyColor>,
			default: "default",
			validator(value: TypographyColor) {
				return COLOR.includes(value);
			}
		},

		/**
		 * 'uppercase' | 'none' | 'lowercase' | 'capitalize'
		 */
		transform: {
			type: String as PropType<TypographyTransform>,
			default: "none",
			validator(value: TypographyTransform) {
				return TRANSFORM.includes(value);
			}
		},

		/**
		 * 'nowrap' | 'wrap' | 'breakall' | 'ellipsis'
		 */
		overflow: {
			type: String as PropType<TypographyOverflow>,
			default: "wrap",
			validator(value: TypographyOverflow) {
				return OVERFLOW.includes(value);
			}
		},

		/**
		 * Is it link? <br/>
		 * true | false
		 */
		link: {
			type: Boolean,
			default: false
		},

		decoration: {
			type: String,
			default: "none"
		},

		/**
		 * is inline
		 *
		 */
		inline: {
			type: Boolean,
			default: false
		},

		highlightText: {
			type: String,
			default: null
		}
	},
	computed: {
		element() {
			//@ts-expect-error
			if (PARAS.indexOf(this.type) > -1) {
				return "p";
			}
			return this.type;
		},

		classes() {
			return [this.type];
		},

		styleObj() {
			const style: CSSProperties = {};

			if (this.decoration) {
				style.textDecoration = this.decoration;
			}

			return style;
		}
	},
	watch: {
		highlightText(textToHighlight) {
			this.initHighlight(textToHighlight);
		}
	},
	mounted() {
		this.initHighlight(this.highlightText);
	},
	methods: {
		initHighlight(text: string) {
			const flowTextRef = this.$refs["flow-text"] as Element;

			unhighlight(flowTextRef);

			if (text) {
				highlight(flowTextRef, text);
			}
		}
	}
});
</script>
<style lang="scss">
@import "../styles/theme-variables/color-names";
$logs-family: var(--log-font);
$number-family: var(--number-font);
$default-family: var(--body-font);
$family: (
	"default": $default-family,
	"number": $number-family,
	"logs": $logs-family
);

$weights: (
	"light": 200,
	"thin": 200,
	"regular": 300,
	"book": 300,
	"medium": 500,
	"bold": 600,
	"black": 700
);
$colors: map-merge(
	$color-names,
	(
		"light": var(--dashboard-font-light),
		"default": var(--dashboard-font),
		"dark": var(--dashboard-font-dark),
		"success": var(--dashboard-success),
		"error": var(--dashboard-danger),
		"warning": var(--dashboard-warning),
		"primary": var(--dashboard-primary),
		"secondary": var(--dashboard-secondary),
		"black": #000000,
		"white": #ffffff,
		"inherit": "inherit"
	)
);
$transforms: uppercase, none, lowercase, capitalize;

.flow-text {
	&.p1 {
		font-size: 14px;
		line-height: 18px;
	}
	&.p2 {
		font-size: 12px;
		line-height: 14px;
	}
	&.p3 {
		font-size: 10px;
		line-height: 12px;
	}
	&.p4 {
		font-size: 8px;
		line-height: 10px;
	}
	&.h1 {
		font-size: 20px;
		line-height: 26px;
	}
	&.h2 {
		font-size: 18px;
		line-height: 22px;
	}
	&.h3 {
		font-size: 16px;
		line-height: 20px;
	}
	&.h4 {
		font-size: 14px;
		line-height: 18px;
	}
	&.h5 {
		font-size: 12px;
		line-height: 16px;
	}
	&.h6 {
		font-size: 10px;
		line-height: 14px;
	}
	&.p1-para {
		font-size: 14px;
		line-height: 18px;
	}
	&.p2-para {
		font-size: 12px;
		line-height: 16px;
	}
	&.p3-para {
		font-size: 10px;
		line-height: 14px;
	}
	&.p4-para {
		font-size: 8px;
		line-height: 12px;
	}
	@each $name, $fam in $family {
		&[data-family="#{$name}"] {
			font-family: $fam;
		}
	}
	@each $name, $weight in $weights {
		&[data-weight="#{$name}"] {
			font-weight: $weight;
		}
	}
	@each $name, $color in $colors {
		&[data-color="#{$name}"] {
			color: $color;
		}
	}
	@each $transform in $transforms {
		&[data-transform="#{$transform}"] {
			text-transform: $transform;
		}
	}

	&[data-overflow="wrap"] {
		white-space: normal;
		word-break: break-word;
	}

	&[data-overflow="breakall"] {
		white-space: normal;
		word-break: break-all;
	}

	&[data-overflow="ellipsis"] {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&[data-overflow="nowrap"] {
		white-space: nowrap;
	}
	&[data-link] {
		cursor: pointer;
	}
	&[data-inline] {
		display: inline;
	}
	mark {
		background-color: transparent;
		color: var(--warning-300);
		font-weight: inherit;
	}
}
</style>
